import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CustomerSlugInput } from "./customer-slug-input";
import { languageName, type LanguageCode } from "@/constants/languages";
import type { AttributeChoice, AttributeValue } from "@/services/category.service";
import {
  ATTRIBUTE_TYPE_LABELS,
  ATTRIBUTE_VALUE_TYPES,
  enumChoicesError,
  type AttributeValueType,
} from "@/lib/categories/attribute-definition";

interface AttributeValueEditorProps {
  id: string;
  name: string;
  valueType: AttributeValueType;
  choices: AttributeChoice[];
  languages: LanguageCode[];
  disabled: boolean;
  onChange: (value: { valueType: AttributeValueType; choices: AttributeChoice[] }) => void;
}

const blankLabels = (languages: readonly string[]) =>
  Object.fromEntries(languages.map((language) => [language, ""]));

export function AttributeValueEditor({
  id,
  name,
  valueType,
  choices,
  languages,
  disabled,
  onChange,
}: AttributeValueEditorProps) {
  const identityError = valueType === "enum" ? enumChoicesError(choices) : null;
  const labelsMissing = choices.some((choice) =>
    languages.some((language) => !choice.label[language]?.trim()),
  );

  const updateChoice = (index: number, patch: Partial<AttributeChoice>) =>
    onChange({
      valueType,
      choices: choices.map((choice, choiceIndex) =>
        choiceIndex === index ? { ...choice, ...patch } : choice,
      ),
    });

  const addChoice = () => {
    const value: AttributeValue =
      valueType === "number" || valueType === "integer" ? 0 : "";
    onChange({
      valueType,
      choices: [...choices, { value, label: blankLabels(languages) }],
    });
  };

  const changeType = (selected: AttributeValueType) => {
    const nextChoices =
      selected === "boolean"
        ? [
            { value: true, label: blankLabels(languages) },
            { value: false, label: blankLabels(languages) },
          ]
        : selected === "enum"
          ? [{ value: "", label: blankLabels(languages) }]
          : [];
    onChange({ valueType: selected, choices: nextChoices });
  };

  const renderChoice = (choice: AttributeChoice, index: number) => (
    <div key={index} className="space-y-3 rounded border p-3">
      <div className="flex items-center gap-2">
        {valueType === "boolean" ? (
          <span className="text-sm font-medium">{String(choice.value)}</span>
        ) : (
          <Input
            type={valueType === "number" || valueType === "integer" ? "number" : "text"}
            step={valueType === "integer" ? "1" : "any"}
            value={String(choice.value)}
            aria-label={`${name} choice ${index + 1}`}
            aria-invalid={!!identityError}
            onChange={(event) =>
              updateChoice(index, {
                value:
                  valueType === "number" || valueType === "integer"
                    ? event.target.value === ""
                      ? 0
                      : Number(event.target.value)
                    : event.target.value,
              })
            }
          />
        )}
        {valueType !== "boolean" ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove ${name} choice ${index + 1}`}
            onClick={() =>
              onChange({ valueType, choices: choices.filter((_, i) => i !== index) })
            }
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {languages.map((language) => (
          <Input
            key={language}
            value={choice.label[language] ?? ""}
            placeholder={`${languageName(language)} label (required)`}
            aria-label={`${name} choice ${index + 1} ${language} label`}
            aria-invalid={!choice.label[language]?.trim()}
            onChange={(event) =>
              updateChoice(index, {
                label: { ...choice.label, [language]: event.target.value },
              })
            }
          />
        ))}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {languages.map((language) => (
          <CustomerSlugInput
            key={language}
            id={`${id}-choice-${index}-slug-${language}`}
            label={`${languageName(language)} value slug`}
            value={choice.slug?.[language] ?? ""}
            disabled={disabled}
            onChange={(value) =>
              updateChoice(index, {
                slug: { ...(choice.slug ?? {}), [language]: value },
              })
            }
          />
        ))}
      </div>
    </div>
  );

  return (
    <fieldset disabled={disabled} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`${id}-type`} className="text-xs text-muted-foreground">
          Value type
        </label>
        <select
          id={`${id}-type`}
          aria-label={`${name} value type`}
          value={valueType}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm disabled:opacity-50"
          onChange={(event) => {
            const selected = ATTRIBUTE_VALUE_TYPES.find(
              (option) => option === event.target.value,
            );
            if (selected) changeType(selected);
          }}
        >
          {ATTRIBUTE_VALUE_TYPES.map((option) => (
            <option key={option} value={option}>{ATTRIBUTE_TYPE_LABELS[option]}</option>
          ))}
        </select>
      </div>

      {valueType === "enum" || valueType === "boolean"
        || valueType === "number" || valueType === "integer" ? (
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          <p className="text-xs text-muted-foreground">
            Configured values have a required shopper label in every catalogue language and optional localized slugs.
          </p>
          {choices.map(renderChoice)}
          {valueType !== "boolean" ? (
            <Button type="button" variant="outline" size="sm" onClick={addChoice}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add configured value
            </Button>
          ) : null}
          {identityError ? (
            <p role="alert" className="text-xs text-destructive">{identityError}</p>
          ) : null}
          {labelsMissing ? (
            <p role="alert" className="text-xs text-destructive">
              Every configured value needs a label in every catalogue language.
            </p>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
}
